/**
 * Friends, presence and invites: the app-wide `/social` connection.
 *
 * Only for signed-in users. `account.svelte.ts` calls `signIn` with the
 * account's access token when there is one and `signOut` when there is not, so
 * a signed-out app never opens this connection at all. While connected it keeps
 * `friends`, `requests` and `invites` current, reconnects on its own, and
 * re-announces what you are doing, so a dropped Wi-Fi does not leave friends
 * seeing you offline for long.
 *
 * Where the server lives is a setting, because the host's embedded signaling
 * server is not it: friends need one address that is always up.
 */

import { PROTOCOL_VERSION } from './protocol'
import { prefs, savePrefs } from './prefs.svelte'
import { normalizeHandle } from './handle'
import type {
  Friend,
  FriendRequest,
  HandleState,
  Person,
  Presence,
  SocialClient,
  SocialErrorCode,
  SocialServer,
} from './social-protocol'

const URL_KEY = 'sofa.social.url'
const MAX_BACKOFF_MS = 15_000
const HEARTBEAT_MS = 15_000
const CONNECTION_TIMEOUT_MS = 45_000

export type FriendsStatus = 'off' | 'connecting' | 'online' | 'offline'

/** How you appear to friends. `handle` is null until you pick one. */
export interface Profile {
  name: string
  handle: string | null
  /** Unix seconds when the handle may change again; 0 when it may now. */
  handleChangeAfter: number
}

export interface HandleCheck {
  handle: string
  state: HandleState
  reason: string | null
  suggestion: string | null
}

/** How long a question to the server may take before we stop waiting. */
const ASK_TIMEOUT_MS = 6000

export interface Invite {
  from: string
  name: string
  code: string
  share_url?: string | null
  at: number
}

/** The server to use when nobody has chosen one. Empty when there is no sensible guess. */
export function defaultSocialUrl(): string {
  const override = import.meta.env.VITE_SOCIAL_URL
  if (override) return override
  if (import.meta.env.DEV) return 'ws://127.0.0.1:8080/social'
  // A page served from the signaling host can use its own origin; the desktop
  // app is served from its own, which has no /social.
  if (typeof location !== 'undefined' && location.protocol.startsWith('http')) {
    const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${scheme}//${location.host}/social`
  }
  return ''
}

function storedUrl(): string {
  try {
    return localStorage.getItem(URL_KEY) ?? defaultSocialUrl()
  } catch {
    return defaultSocialUrl()
  }
}

/** Accept `ws://host:8080`, a bare host, or a full `/social` URL. */
export function normalizeSocialUrl(input: string): string {
  let text = input.trim()
  if (!text) return ''
  if (!/^wss?:\/\//i.test(text)) {
    text = `${/^(localhost|127\.|\[::1\])/i.test(text) ? 'ws' : 'wss'}://${text}`
  }
  return text.replace(/\/+$/, '').replace(/\/(ws|social)$/, '') + '/social'
}

const REFUSALS: Partial<Record<SocialErrorCode, string>> = {
  not_friends: 'You are not friends yet.',
  not_online: 'They are offline.',
  no_such_request: 'That request is gone.',
  too_many: 'Too many friends or requests.',
  bad_request: 'The server did not understand that.',
  invalid_token: 'Your login is not valid any more. Sign in again.',
  handle_taken: 'Someone already has that handle.',
  handle_invalid: 'That handle is not allowed.',
  handle_cooldown: 'You changed your handle recently. Try again later.',
  accounts_unavailable: 'This server cannot check logins right now.',
}

class Friends {
  status = $state<FriendsStatus>('off')
  friends = $state<Friend[]>([])
  requests = $state<FriendRequest[]>([])
  invites = $state<Invite[]>([])
  /** The last thing that went wrong, in words a person can act on. */
  notice = $state<string | null>(null)
  url = $state(storedUrl())
  /** Your account id, once signed in; what your friend link carries. */
  userId = $state('')
  /** Your own name and handle, once the server has said them. */
  me = $state<Profile | null>(null)
  /** The nickname this device used before the account existed, to prefill setup. */
  nameHint = $state('')

  /** False against a server too old to know handles: setup would only fail there. */
  #profiles = true
  #profileWaiter: { resolve: () => void; reject: (reason: Error) => void } | null = null
  #handleWaiters = new Map<string, ((check: HandleCheck) => void)[]>()
  #searchWaiters = new Map<string, ((person: Person | null) => void)[]>()

  #socket: WebSocket | null = null
  #token: string | null = null
  #presence: Presence = { state: 'online' }
  #attempt = 0
  #timer: ReturnType<typeof setTimeout> | null = null
  #heartbeat: ReturnType<typeof setInterval> | null = null
  /** Set when the server said the login is bad or replaced; stops the retry loop. */
  #halted = false

  /** Sorted for the list: sharing first, then online, then offline, then by name. */
  get sorted(): Friend[] {
    const rank = (f: Friend) =>
      f.presence.state === 'sharing' ? 0 : f.presence.state === 'online' ? 1 : 2
    return [...this.friends].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
  }

  /** Signed in and connected, but no handle chosen yet: setup is due. */
  get needsHandle(): boolean {
    return this.#profiles && this.status === 'online' && this.me !== null && this.me.handle === null
  }

  /** Requests plus invites: what deserves a badge. */
  get attention(): number {
    return this.requests.length + this.invites.length
  }

  /**
   * Connect with this account's access token. Safe to call again whenever the
   * token is refreshed: reconnect immediately so the server's expiry and
   * deletion checks use the current token.
   */
  signIn(accessToken: string): void {
    const fresh = accessToken !== this.#token
    this.#token = accessToken
    // A new token is a second chance after a rejected or replaced login.
    if (fresh && this.#halted) this.#halted = false
    if (fresh && this.#socket) {
      this.#stopHeartbeat()
      const previous = this.#socket
      this.#socket = null
      previous.close()
    }
    if (fresh && this.#timer) {
      clearTimeout(this.#timer)
      this.#timer = null
    }
    if (!this.url) {
      this.status = 'off'
      return
    }
    if (this.#halted) return
    if (this.#socket && this.#socket.readyState <= WebSocket.OPEN) return
    this.#open()
  }

  /** Disconnect and forget everything: signed out, so no friends. */
  signOut(): void {
    this.#stopHeartbeat()
    this.#token = null
    this.#halted = false
    this.#attempt = 0
    if (this.#timer) clearTimeout(this.#timer)
    this.#timer = null
    this.#socket?.close()
    this.#socket = null
    this.status = 'off'
    this.friends = []
    this.requests = []
    this.invites = []
    this.userId = ''
    this.me = null
    this.nameHint = ''
    this.notice = null
    this.#profileWaiter?.reject(new Error('Signed out'))
    this.#profileWaiter = null
    for (const waiters of this.#handleWaiters.values()) {
      for (const done of waiters)
        done({ handle: '', state: 'invalid', reason: null, suggestion: null })
    }
    this.#handleWaiters.clear()
    for (const waiters of this.#searchWaiters.values()) for (const done of waiters) done(null)
    this.#searchWaiters.clear()
  }

  setUrl(input: string): void {
    this.url = normalizeSocialUrl(input)
    try {
      if (this.url) localStorage.setItem(URL_KEY, this.url)
      else localStorage.removeItem(URL_KEY)
    } catch {
      // The choice lasts for this session.
    }
    const token = this.#token
    this.signOut()
    if (token) this.signIn(token)
  }

  /** Tell friends what you are doing. Remembered across reconnects. */
  setPresence(presence: Presence): void {
    if (
      presence.state === this.#presence.state &&
      (presence.state !== 'sharing' ||
        (this.#presence.state === 'sharing' &&
          presence.code === this.#presence.code &&
          presence.share_url === this.#presence.share_url))
    )
      return
    this.#presence = presence
    if (this.status === 'online') this.#send({ type: 'set_presence', presence })
  }

  request(userId: string): void {
    this.#send({ type: 'friend_request', to: userId })
  }

  accept(userId: string): void {
    this.#send({ type: 'accept_friend', from: userId })
  }

  decline(userId: string): void {
    this.#send({ type: 'decline_friend', from: userId })
  }

  remove(userId: string): void {
    this.#send({ type: 'remove_friend', user_id: userId })
  }

  invite(userId: string, code: string, shareUrl?: string): void {
    this.#send({ type: 'invite', to: userId, code, share_url: shareUrl })
  }

  /**
   * Set your display name and, optionally, your handle. Resolves once the
   * server has saved it; rejects with the reason in words a person can act on.
   */
  setProfile(name: string, handle: string | null): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.status !== 'online') {
        reject(new Error('Not connected to the friends server yet.'))
        return
      }
      this.#profileWaiter?.reject(new Error('Replaced by a newer change.'))
      const timer = setTimeout(() => {
        if (this.#profileWaiter?.reject === fail) this.#profileWaiter = null
        reject(new Error('The server did not answer. Try again.'))
      }, ASK_TIMEOUT_MS)
      const fail = (reason: Error) => {
        clearTimeout(timer)
        reject(reason)
      }
      this.#profileWaiter = {
        resolve: () => {
          clearTimeout(timer)
          resolve()
        },
        reject: fail,
      }
      this.#send({ type: 'set_profile', name: name.trim(), handle })
    })
  }

  /** Whether `handle` is free. Resolves with `invalid` when the server cannot be asked. */
  checkHandle(handle: string): Promise<HandleCheck> {
    const key = normalizeHandle(handle)
    return new Promise((resolve) => {
      if (this.status !== 'online') {
        resolve({ handle: key, state: 'invalid', reason: 'Not connected', suggestion: null })
        return
      }
      const waiters = this.#handleWaiters.get(key) ?? []
      waiters.push(resolve)
      this.#handleWaiters.set(key, waiters)
      setTimeout(() => {
        const left = this.#handleWaiters.get(key)
        if (!left?.includes(resolve)) return
        this.#handleWaiters.set(
          key,
          left.filter((w) => w !== resolve),
        )
        resolve({
          handle: key,
          state: 'invalid',
          reason: 'No answer. Try again.',
          suggestion: null,
        })
      }, ASK_TIMEOUT_MS)
      this.#send({ type: 'check_handle', handle: key })
    })
  }

  /** Find a person by their exact handle. Null when nobody has it. */
  search(handle: string): Promise<Person | null> {
    const key = normalizeHandle(handle)
    return new Promise((resolve) => {
      if (this.status !== 'online') {
        resolve(null)
        return
      }
      const waiters = this.#searchWaiters.get(key) ?? []
      waiters.push(resolve)
      this.#searchWaiters.set(key, waiters)
      setTimeout(() => {
        const left = this.#searchWaiters.get(key)
        if (!left?.includes(resolve)) return
        this.#searchWaiters.set(
          key,
          left.filter((w) => w !== resolve),
        )
        resolve(null)
      }, ASK_TIMEOUT_MS)
      this.#send({ type: 'search', handle: key })
    })
  }

  deleteAccount(): void {
    this.#send({ type: 'delete_account' })
  }

  dismissInvite(at: number): void {
    this.invites = this.invites.filter((i) => i.at !== at)
  }

  #send(message: SocialClient): void {
    if (this.#socket?.readyState === WebSocket.OPEN) this.#socket.send(JSON.stringify(message))
  }

  #stopHeartbeat(): void {
    if (this.#heartbeat !== null) clearInterval(this.#heartbeat)
    this.#heartbeat = null
  }

  #open(): void {
    this.status = this.#attempt === 0 ? 'connecting' : 'offline'
    let socket: WebSocket
    try {
      socket = new WebSocket(this.url)
    } catch {
      this.status = 'off'
      this.notice = 'That server address is not valid.'
      return
    }
    this.#socket = socket
    let lastResponse = Date.now()

    socket.onopen = () => {
      if (this.#socket !== socket) return
      if (!this.#token) return
      // The name lives on the account now; an empty one means "keep mine".
      this.#send({
        type: 'hello',
        access_token: this.#token,
        name: '',
        protocol_version: PROTOCOL_VERSION,
      })
    }
    socket.onmessage = (event) => {
      if (this.#socket !== socket) return
      if (typeof event.data !== 'string') return
      try {
        this.#handle(JSON.parse(event.data) as SocialServer)
        lastResponse = Date.now()
      } catch {
        // A frame that is not JSON is not ours to act on.
      }
    }
    const disconnected = () => {
      if (this.#socket !== socket) return
      this.#stopHeartbeat()
      this.#socket = null
      if (!this.#token || this.#halted) return
      this.status = 'offline'
      // Presence is the server's to clear: it marks us offline when this closes.
      const delay = Math.min(1000 * 2 ** this.#attempt, MAX_BACKOFF_MS)
      this.#attempt += 1
      this.#timer = setTimeout(() => {
        this.#timer = null
        if (this.#token && !this.#halted) this.#open()
      }, delay)
    }
    socket.onclose = disconnected
    this.#stopHeartbeat()
    this.#heartbeat = setInterval(() => {
      if (this.#socket !== socket) return
      if (Date.now() - lastResponse >= CONNECTION_TIMEOUT_MS) {
        // A half-open socket may never deliver onclose, including after sleep.
        disconnected()
        socket.close()
      } else if (this.status === 'online') {
        this.#send({ type: 'ping', nonce: Date.now() })
      }
    }, HEARTBEAT_MS)
  }

  #adoptProfile(ready: Extract<SocialServer, { type: 'ready' }>): void {
    this.#profiles = 'handle' in ready
    const name = ready.name ?? ''
    const handle = ready.handle ?? null
    this.me = { name, handle, handleChangeAfter: ready.handle_change_after ?? 0 }
    if (handle === null) {
      // The account's own name (from Google) beats a nickname left by whoever used
      // this device before; "Friend" is only the server's placeholder.
      this.nameHint = name && name !== 'Friend' ? name : prefs.displayName.trim() || name
    } else if (name && prefs.displayName !== name) {
      // The account's name wins everywhere, including the name shown in rooms.
      prefs.displayName = name
      savePrefs()
    }
    this.#profileWaiter?.resolve()
    this.#profileWaiter = null
  }

  #handle(message: SocialServer): void {
    switch (message.type) {
      case 'account_deleted':
        this.signOut()
        window.dispatchEvent(new Event('sofa-account-deleted'))
        return
      case 'ready':
        this.#attempt = 0
        this.status = 'online'
        this.notice = null
        this.userId = message.user_id
        this.friends = message.friends
        this.requests = message.requests
        this.#adoptProfile(message)
        if (this.#presence.state !== 'online')
          this.#send({ type: 'set_presence', presence: this.#presence })
        return
      case 'profile_changed':
        this.friends = this.friends.map((f) =>
          f.user_id === message.user_id ? { ...f, name: message.name, handle: message.handle } : f,
        )
        return
      case 'handle_status': {
        const waiters = this.#handleWaiters.get(message.handle) ?? []
        this.#handleWaiters.delete(message.handle)
        const check: HandleCheck = {
          handle: message.handle,
          state: message.state,
          reason: message.reason ?? null,
          suggestion: message.suggestion ?? null,
        }
        for (const done of waiters) done(check)
        return
      }
      case 'search_result': {
        const waiters = this.#searchWaiters.get(message.handle) ?? []
        this.#searchWaiters.delete(message.handle)
        for (const done of waiters) done(message.person)
        return
      }
      case 'friend_request':
        if (!this.requests.some((r) => r.user_id === message.request.user_id)) {
          this.requests = [...this.requests, message.request]
        }
        return
      case 'friend_added':
        this.requests = this.requests.filter((r) => r.user_id !== message.friend.user_id)
        this.friends = [
          ...this.friends.filter((f) => f.user_id !== message.friend.user_id),
          message.friend,
        ]
        return
      case 'friend_removed':
        this.friends = this.friends.filter((f) => f.user_id !== message.user_id)
        return
      case 'presence_changed':
        this.friends = this.friends.map((f) =>
          f.user_id === message.user_id ? { ...f, presence: message.presence } : f,
        )
        return
      case 'invite':
        this.invites = [
          {
            from: message.from,
            name: message.name,
            code: message.code,
            share_url: message.share_url,
            at: Date.now(),
          },
          ...this.invites.filter((invite) => invite.from !== message.from),
        ].slice(0, 20)
        return
      case 'error':
        if (message.code === 'auth_failed' && message.message.includes('somewhere else')) {
          this.#halted = true
          this.status = 'off'
          this.notice = 'You signed in on another device, so this one was disconnected.'
          return
        }
        if (
          message.code === 'handle_taken' ||
          message.code === 'handle_invalid' ||
          message.code === 'handle_cooldown'
        ) {
          // Belongs to the form that asked, not to the page's notice bar.
          const waiter = this.#profileWaiter
          this.#profileWaiter = null
          waiter?.reject(new Error(REFUSALS[message.code] ?? message.message))
          if (!waiter) this.notice = REFUSALS[message.code] ?? message.message
          return
        }
        if (message.code === 'invalid_token' || message.code === 'protocol_mismatch') {
          // Retrying with the same token or version cannot help.
          this.#halted = true
          this.status = 'off'
        }
        this.notice = REFUSALS[message.code] ?? message.message
        return
      case 'pong':
        return
    }
  }
}

export const friends = new Friends()

/** A short, stable id for a friend row, for display next to duplicate names. */
export function shortId(userId: string): string {
  return userId.slice(0, 6)
}
